
import { useCallback } from 'react';
import { UserCategory } from '@/types';
import { categorySyncService } from '@/services/categories/categorySyncService';
import { categorySpecificSyncService } from '@/services/categories/categorySpecificSync.service';
import { queryClient } from '@/services/queryClient';
import { useCategoriesSync } from '../useCategoriesSync';
import OfflineLogger from '@/utils/offlineLogger';

interface UseCategorySyncOptions {
  userId?: string;
  setCategories: (categories: UserCategory[]) => void;
  updatePendingCount: () => Promise<void>;
}

export const useCategorySync = ({ userId, setCategories, updatePendingCount }: UseCategorySyncOptions) => {
  const { syncCategories, isSyncing, lastSyncTimestamp } = useCategoriesSync();

  const forceSyncCategories = useCallback(async () => {
    if (!userId) return;
    
    try {
      const syncedCategories = await syncCategories();
      setCategories(syncedCategories);
      
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-categories'] }),
        queryClient.invalidateQueries({ queryKey: ['user-categories-offline'] }),
        updatePendingCount()
      ]);
      
      OfflineLogger.info('useCategorySync.forceSyncCategories', 'Force sync completed');
    } catch (error) {
      OfflineLogger.error('useCategorySync.forceSyncCategories', error);
    }
  }, [userId, updatePendingCount, syncCategories, setCategories]);

  const syncOnlyCategories = useCallback(async () => {
    if (!userId) return;
    
    try {
      const syncedCategories = await categorySpecificSyncService.syncOnlyCategories();
      setCategories(syncedCategories);
      await updatePendingCount();
      
      OfflineLogger.info('useCategorySync.syncOnlyCategories', 'Category-specific sync completed');
    } catch (error) {
      OfflineLogger.error('useCategorySync.syncOnlyCategories', error);
    }
  }, [userId, updatePendingCount, setCategories]);

  const updatePendingCountFromService = useCallback(async () => {
    try {
      const count = await categorySyncService.getPendingOperationsCount();
      return count;
    } catch (error) {
      OfflineLogger.error('useCategorySync.updatePendingCountFromService', error);
      return 0;
    }
  }, []);

  return {
    forceSyncCategories,
    syncOnlyCategories,
    updatePendingCountFromService,
    isSyncing,
    lastSyncTimestamp
  };
};
